import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAccount, useBalance, useReadContract, useWaitForTransactionReceipt, useWriteContract } from "wagmi";
import { BIGCOINMAIN_ADDRESS, BIGCOINTOKEN_ADDRESS, Miner, miners, TOKEN_SYMBOL } from "../../../utils/constants";
import { erc20Abi, formatUnits, parseUnits, zeroAddress } from "viem";
import { notifyError, notifySuccess } from "../../../hooks/utils";
import BigcoinTokenContractABI from "../../../abis/bigcointoken.abi.json";
import BigcoinMainContractABI from "../../../abis/bigcoinmain.abi.json";
import "./buyminermodal.css";
import { BigNumber } from "ethers";
import { useTranslation } from "react-i18next";

interface BuyMinerModalProps {
  open: boolean;
  onClose: () => void;
  selectedPosition: { x: number; y: number } | null;
  availableEnergy: number;
}

const BuyMinerModal: React.FC<BuyMinerModalProps> = ({ open, onClose, selectedPosition, availableEnergy }) => {
  const { t, i18n } = useTranslation();
  const account = useAccount();


  const [activeTab, setActiveTab] = React.useState<'miners' | 'flex'>('miners');
  const [txType, setTxType] = useState<"approve" | "buy"| null>(null);

  // if (!open) return null;

  const list = activeTab === 'miners'
    ? miners.filter((miner) => miner.availableToBuy && !miner.isFlex)
    : miners.filter((miner) => miner.availableToBuy && miner.isFlex);

  const [selectedMiner, setSelectedMiner] = React.useState(list[0].id);

  // Reset selected item when tab changes
  React.useEffect(() => {
    setSelectedMiner(list[0].id);
  }, [activeTab]);

  const item = useMemo(() => {
    return miners.find((miner) => miner.id === selectedMiner) as Miner ?? list[0];
  }, [selectedMiner, list]);

  const [buyingItem, setBuyingItem] = useState(false);

  const { data: hash, error, isPending, writeContract } = useWriteContract();
  const { isLoading: isConfirming, isSuccess: isConfirmed } = useWaitForTransactionReceipt({
    hash,
  });

  const userHasEnoughEnergy = useMemo(() => {
    // check if the item.energy is less than or equal to the availableSpaces
    return item.energy <= availableEnergy;
  }, [item.energy, availableEnergy]);

  // Read the allowance and refetch on demand
  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    abi: erc20Abi,
    address: BIGCOINTOKEN_ADDRESS,
    functionName: "allowance",
    args: [account ? account.address as any : zeroAddress, BIGCOINMAIN_ADDRESS],
  });

  const [hasSufficientAllowance, setHasSufficientAllowance] = useState(false);
  const [isApproving, setApproving] = useState(false);

  const handleApproveTokens = async () => {
    if (!account) {
      // Redirect to login or show a message
      notifyError("You need to connect wallet first.");
      return;
    }

    try {
      setTxType("approve");
      setApproving(true);

      const transactionArgs = {
        abi: erc20Abi,
        address: BIGCOINTOKEN_ADDRESS,
        functionName: 'approve',
        args: [BIGCOINMAIN_ADDRESS, parseUnits(item.price.toString(), 18)],
      };

      writeContract(transactionArgs as any);
    } catch (error) {
      notifyError("An error occurred while trying to approve.");
    } finally {
      setApproving(false);
    }
  };
  useEffect(() => {
    if (allowance && item) {
      const isAllowed = BigNumber.from(allowance).gte(parseUnits(item.price.toString(), 18));
      setHasSufficientAllowance(isAllowed);
    } else {
      setHasSufficientAllowance(false); // Reset when no allowance or trade value
    }
  }, [allowance, item]); // Update hasSufficientAllowance when allowance or trade value changes

  useEffect(() => {
    // Refetch allowance automatically when input trade value changes
    refetchAllowance();
  }, [item]); // Optionally refetch allowance when input value changes

  useEffect(() => {
    // Refetch allowance when the transaction is confirmed
    if (isConfirmed) {
      refetchAllowance();
    }
  }, [isConfirmed, refetchAllowance]);


  const { data: bigTokenBalance } = useReadContract({
    address: BIGCOINTOKEN_ADDRESS,
    abi: BigcoinTokenContractABI,
    functionName: 'balanceOf',
    args: [account ? account.address : zeroAddress as any]
  });

  const { data: userAlreadyGotFreeStarterMiner } = useReadContract({
    address: BIGCOINMAIN_ADDRESS,
    abi: BigcoinMainContractABI,
    functionName: 'acquiredStarterMiner',
    args: [account ? account.address : zeroAddress as any]
  });

  const bigTokenBalanceFormatted = useMemo(() => {
    if (bigTokenBalance) {
      return Number(formatUnits(bigTokenBalance as any, 18));
    }
    return 0;
  }, [bigTokenBalance]);

  const userHasEnoughBig = useMemo(() => {
    return bigTokenBalanceFormatted >= item.price;
  }
    , [bigTokenBalanceFormatted, item.price]);

  // Log the transaction arguments and hash for debugging
  // useEffect(() => {
  //   if (hash) {
  //     console.log("Transaction hash:", hash);
  //     notifySuccess("Buying item!");
  //   }
  //   if (error) {
  //     console.error("Error during contract write:", error);
  //     notifyError("An error occurred while trying to buy item.");
  //   }
  //   if (isConfirmed) {
  //     console.log("Transaction confirmed!");
  //     notifySuccess("Bought item!");
  //   }
  // }, [hash, error]);


  useEffect(() => {
    if (hash) {
      switch (txType) {
        case "approve":
          console.log("Transaction hash:", hash);
          notifySuccess("Approval transaction sent.");
          break;
        case "buy":
          console.log("Transaction hash:", hash);
          notifySuccess("Buy transaction sent.");
          break;
        default:
          console.log("Transaction hash:", hash);
          notifySuccess("Transaction sent.");
      }
    }
    if (error) {
      switch (txType) {
        case "approve":
          console.error("Error during contract write:", error);
          notifyError("Failed to approve.");
          break;
        case "buy":
          console.error("Error during contract write:", error);
          notifyError("Failed to buy item.");
          break;
        default:
          console.error("Error during contract write:", error);
          notifyError("Transaction failed.");
      }
    }
    if (isConfirmed) {
      switch (txType) {
        case "approve":
          notifySuccess("Approval successful.");
          break;
        case "buy":
          notifySuccess("Item bought!");
          break;
        default:
          notifySuccess("Transaction confirmed.");
      }

      // Optional: Reset txType after confirmation
      setTxType(null);
    }
  }, [hash, error, isConfirmed]);



  const handleBuyItem = async () => {
    if (!account) {
      // Redirect to login or show a message
      notifyError("You need to connect wallet first.");
      return;
    }

    try {
      setTxType("buy");
      setBuyingItem(true);

      let transactionArgs;

      if (item.id === 1) {
        transactionArgs = {
          address: BIGCOINMAIN_ADDRESS,
          abi: BigcoinMainContractABI,
          functionName: 'getFreeStarterMiner',
          args: [selectedPosition?.x ?? 0, selectedPosition?.y ?? 0],
        };
      } else {
        transactionArgs = {
          address: BIGCOINMAIN_ADDRESS,
          abi: BigcoinMainContractABI,
          functionName: 'buyMiner',
          args: [item.id, selectedPosition?.x ?? 0, selectedPosition?.y ?? 0],
        };
      }

      writeContract(transactionArgs as any);
    } catch (error) {
      notifyError("An error occurred while trying to buy item.");
    } finally {
      setBuyingItem(false);
    }
  };


  // MODAL OPEN AND CLOSE
  const modalRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div data-state="open" className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" style={{ pointerEvents: "auto" }} data-aria-hidden="true" aria-hidden="true">
      <div
        ref={modalRef}
        role="dialog"
        id="radix-:r4f:"
        aria-describedby="radix-:r4h:"
        aria-labelledby="radix-:r4g:"
        data-state="open"
        className="text-sm fixed left-[50%] top-[50%] z-50 grid w-full max-w-modal translate-x-[-50%] translate-y-[-50%] gap-4 border border-slate-200 p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg dark:border-slate-800 dark:bg-slate-950 bg-primary text-white"
        tabIndex={-1}
        style={{ pointerEvents: "auto", border: "none", background: "rgb(16, 44, 57)" }}
      >


        <div className="sc-aa05954f-3 gnIWBN">
          <div className="sc-8ebf3da6-0 gNKziR">
            <h2 className="sc-175a34af-0 fXNxOx">{t("buy_miner.buy_item")}</h2>
            <div className="sc-175a34af-1 iwtenU">
              <button onClick={() => setActiveTab("miners")} className={`sc-175a34af-2 ${activeTab === "miners" ? "eAwgWW" : "bfMGiG"}`}>{t("buy_miner.animals")}</button>
              <button onClick={() => setActiveTab("flex")} className={`sc-175a34af-2 ${activeTab === "flex" ? "eAwgWW" : "bfMGiG"}`}>{t("buy_miner.flex_shop")}</button>
            </div>
            <div className="sc-175a34af-3 hBMNac">
              <p className="sc-175a34af-4 bsJBwJ">{t("buy_miner.selected_location")}: ({selectedPosition?.x ?? 0}, {selectedPosition?.y ?? 0})</p>
            </div>
            <div className="sc-ad29d9f1-0 ftAKqb">

              <div className="sc-ad29d9f1-1 diLTTI">
                {(!userAlreadyGotFreeStarterMiner && activeTab === "miners") && (
                  <div className={`sc-dece7ae5-0 ${selectedMiner === miners[0].id ? "CgQeU" : "ddkPJh"}`} onClick={() => setSelectedMiner(miners[0].id)} key={miners[0].id}>
                    <div className="sc-dece7ae5-1 zTKYE">
                      <img
                        src={miners[0].img} alt={miners[0].name}
                        className="sc-dece7ae5-2 gtqptc"
                      />
                    </div>
                    <span>{miners[0].name}</span>
                  </div>
                )}
                {list.map((miner: Miner, idx: number) => (
                  miner.isFlex ? (
                    <div className={`sc-861a5b5a-0 ${selectedMiner === miner.id ? "ljLawE" : "kWaOhY"}`} onClick={() => setSelectedMiner(miner.id)} key={miner.id}>
                      <span className="sc-861a5b5a-1 bJOSEz">{miner.name}</span>
                    </div>
                  ) : (
                    <div className={`sc-dece7ae5-0 ${selectedMiner === miner.id ? "CgQeU" : "ddkPJh"}`} onClick={() => setSelectedMiner(miner.id)} key={miner.id}>
                      <div className="sc-dece7ae5-1 zTKYE">
                        <img
                          src={miner.img} alt={miner.name}
                          className="sc-dece7ae5-2 gtqptc"
                        />
                      </div>
                      <span>{miner.name}</span>
                    </div>
                  )
                ))}
              </div>
              <div className="sc-ad29d9f1-2 hYubbd">
                <h3 className="sc-ad29d9f1-3 eaSCTr">{item.name}</h3>
                <div className="sc-ad29d9f1-4 eRsHyl" style={{ marginTop: "-1rem" }} >
                  <img
                    src={item.img}
                    alt={item.name}
                    className="sc-ad29d9f1-8 eHUdVf"
                    style={{ width: "150px", height: "150px" }}
                  />
                </div>
                <div className="sc-ad29d9f1-5 juJbej" style={{ marginTop: "-1rem" }}                >
                  <p className="sc-ad29d9f1-6 lffYpc" style={{ textAlign: "left" }}>{t("buy_miner.milk_power")}: {item.hashRate} L/s</p>
                  <p className="sc-ad29d9f1-6 lffYpc" style={{ textAlign: "left" }}>{t("buy_miner.price")}: {item.price} ${TOKEN_SYMBOL}</p>
                  <p className="sc-ad29d9f1-6 lffYpc" style={{ textAlign: "left" }}>{t("buy_miner.food")}: {item.energy} {t("buy_miner.food")}</p>
                  {activeTab === "miners" && (userHasEnoughEnergy ? (userHasEnoughBig ? null : (<p className="sc-ad29d9f1-7 gFOpHZ" style={{ textAlign: "left" }}>{t("buy_miner.insufficient_milk")}</p>)) : (<p className="sc-ad29d9f1-7 gFOpHZ" style={{ textAlign: "left" }}>{t("buy_miner.insufficient_food")}</p>))}
                  {activeTab === "flex" && <p className="sc-ad29d9f1-6 lffYpc" style={{ textAlign: "left" }}>{item.description}</p>}
                </div>
              </div>
            </div>
            <div className="sc-ad29d9f1-9 jjLaSz">
              <button onClick={onClose} className="sc-cdc39d4e-0 gvrgXY">{t("buy_miner.cancel")}</button>
              {selectedMiner !== 1 ? hasSufficientAllowance ? (
                <button onClick={handleBuyItem} disabled={buyingItem || isPending || isConfirming || !userHasEnoughBig || !userHasEnoughEnergy} className={`sc-cdc39d4e-0 ${buyingItem || isPending || isConfirming || !userHasEnoughBig || !userHasEnoughEnergy ? "ivYpDu" : "glfaWa"}`}>
                  {userHasEnoughBig ? (buyingItem || isPending || isConfirming) ? t("buy_miner.processing") : t("buy_miner.buy_now") : t("buy_miner.insufficient_balance")}
                </button>
              ) : (
                <button onClick={handleApproveTokens} disabled={isApproving || isPending || isConfirming || !userHasEnoughBig || !userHasEnoughEnergy} className={`sc-cdc39d4e-0 ${isApproving || isPending || isConfirming || !userHasEnoughBig || !userHasEnoughEnergy ? "ivYpDu" : "glfaWa"}`}>
                  {userHasEnoughBig ? (isApproving || isPending || isConfirming) ? t("buy_miner.approving") : t("buy_miner.approve") : t("buy_miner.insufficient_balance")}
                </button>
              ) : (
                <button onClick={handleBuyItem} disabled={buyingItem || isPending || isConfirming || !userHasEnoughEnergy} className={`sc-cdc39d4e-0 ${buyingItem || isPending || isConfirming || !userHasEnoughEnergy ? "ivYpDu" : "glfaWa"}`}>
                  {(buyingItem || isPending || isConfirming) ? t("buy_miner.processing") : t("buy_miner.get_free_animal")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
export default BuyMinerModal;